import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Tooltip,
} from '@mui/material';
import {
  Delete,
  Add,
  Group,
  UploadFile,
  Sell as PriceListIcon
} from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import { PriceList, PriceListImportResult } from '../../types';
import { useAuth } from '../../context/AuthContext';

// Restructurado 2026-09-05 (worker brief "price-list-management-restructure"):
// esta pantalla es ahora solo el nivel "lista de listas" - crear/activar-
// desactivar/eliminar listas, más un disparador de importación por lista
// directo desde la fila. La gestión de precios de una lista (agregar/editar/
// eliminar entradas, importar Excel con detalle completo, panel de
// "productos sin precio", alertas cross-list) vive en su propia página de
// detalle (`PriceListDetail.tsx`, ruta `/admin/price-lists/:id`) - se llega
// ahí haciendo click en la fila o en el nombre de la lista, ya no abriendo
// un modal ("Gestionar precios") desde un lápiz.
export const PriceListManagement: React.FC = () => {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [priceLists, setPriceLists] = useState<PriceList[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Create list dialog
  const [createDialog, setCreateDialog] = useState(false);
  const [newListName, setNewListName] = useState('');

  // Importación CSV/XLS directa por fila (worker brief
  // "price-list-management-restructure") - el resultado se muestra en un
  // diálogo resumen; el detalle completo (alertas cross-list, panel de
  // productos sin precio) se revisa entrando a la página de la lista.
  const [importingListId, setImportingListId] = useState<number | null>(null);
  const [importResultDialog, setImportResultDialog] = useState<{ list: PriceList; result: PriceListImportResult } | null>(null);

  const loadPriceLists = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.priceLists.getAll();
      setPriceLists(data);
    } catch (err: any) {
      console.error('Error loading price lists:', err);
      setError('Error al cargar las listas de precios');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPriceLists();
  }, [loadPriceLists]);

  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError(null);
        setSuccess(null);
      }, 6000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  // --- Create / toggle / delete list ---

  const handleCreateList = async () => {
    if (!newListName.trim()) return;
    try {
      await apiService.priceLists.create(newListName.trim());
      setSuccess('Lista de precios creada');
      setCreateDialog(false);
      setNewListName('');
      loadPriceLists();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al crear la lista');
    }
  };

  const handleToggleActive = async (list: PriceList) => {
    try {
      await apiService.priceLists.update(list.id, { is_active: !list.is_active });
      loadPriceLists();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al actualizar la lista');
    }
  };

  const handleDeleteList = async (list: PriceList) => {
    if (!window.confirm(`¿Eliminar la lista "${list.name}"? Solo es posible si no tiene entradas.`)) return;
    try {
      await apiService.priceLists.delete(list.id);
      setSuccess('Lista de precios eliminada');
      loadPriceLists();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'No se pudo eliminar la lista (¿tiene entradas?)');
    }
  };

  // Disparador de importación por lista, directo desde la fila (reemplaza el
  // botón "Importar Excel" que vivía dentro del modal "Gestionar precios").
  const handleImportForList = async (list: PriceList, file: File) => {
    try {
      setImportingListId(list.id);
      setError(null);
      const result = await apiService.priceLists.importExcel(list.id, file);
      setImportResultDialog({ list, result });
    } catch (err: any) {
      setError(err.response?.data?.detail || `Error al importar el archivo para "${list.name}"`);
    } finally {
      setImportingListId(null);
    }
  };

  const goToDetail = (list: PriceList) => {
    navigate(`/admin/price-lists/${list.id}`);
  };

  if (currentUser?.role !== 'admin') {
    return (
      <Box p={3}>
        <Alert severity="error">
          No tenés permisos para acceder a esta sección. Solo los administradores pueden gestionar listas de precios.
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Listas de Precios
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setCreateDialog(true)}
          size="large"
        >
          Crear Lista
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Listas ({priceLists.length})
          </Typography>

          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Nombre</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Usuarios Asignados</TableCell>
                    <TableCell align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {priceLists.map((list) => (
                    <TableRow
                      key={list.id}
                      hover
                      onClick={() => goToDetail(list)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <PriceListIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                          <Typography
                            component="span"
                            sx={{ fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}
                          >
                            {list.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={list.is_active}
                              onChange={() => handleToggleActive(list)}
                              size="small"
                            />
                          }
                          label={list.is_active ? 'Activa' : 'Inactiva'}
                        />
                      </TableCell>
                      <TableCell>
                        {/* Solo lectura desde 2026-09-04 (CLAUDE.md §4) - la
                            edición real de a qué usuarios se les asigna esta
                            lista, y su tope de descuento, vive en Gestión de
                            Usuarios ahora. Se mantiene acá como informativo:
                            no está atada a ninguna acción de esta pantalla,
                            pero sigue siendo un resumen útil de un vistazo
                            (ver HANDOFF.md para el detalle de esta decisión). */}
                        <Tooltip
                          title={
                            (list.assigned_users || []).length > 0
                              ? (list.assigned_users || []).map((a) => a.name).join(', ')
                              : 'Sin usuarios asignados - asignar desde Gestión de Usuarios'
                          }
                        >
                          <Chip
                            icon={<Group fontSize="small" />}
                            label={`Asignada a ${(list.assigned_user_ids || []).length} usuario(s)`}
                            size="small"
                            variant="outlined"
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="Subir CSV/XLS a esta lista">
                          <span>
                            <IconButton
                              component="label"
                              size="small"
                              color="primary"
                              disabled={importingListId === list.id}
                            >
                              {importingListId === list.id ? (
                                <CircularProgress size={18} />
                              ) : (
                                <UploadFile fontSize="small" />
                              )}
                              <input
                                type="file"
                                hidden
                                accept=".xlsx,.xls"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) handleImportForList(list, file);
                                  e.target.value = '';
                                }}
                              />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Eliminar lista">
                          <IconButton size="small" color="error" onClick={() => handleDeleteList(list)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {priceLists.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No hay listas de precios en este país
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Create list dialog */}
      <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Crear Lista de Precios</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            autoFocus
            label="Nombre"
            value={newListName}
            onChange={(e) => setNewListName(e.target.value)}
            placeholder="Ej: Mayoristas"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreateList} disabled={!newListName.trim()}>
            Crear
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resultado de la importación por fila */}
      <Dialog open={importResultDialog !== null} onClose={() => setImportResultDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Resultado de la importación - {importResultDialog?.list.name}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="success.main">
            • Creadas: {importResultDialog?.result.created ?? 0}
          </Typography>
          <Typography variant="body2" color="info.main">
            • Actualizadas: {importResultDialog?.result.updated ?? 0}
          </Typography>
          {importResultDialog && importResultDialog.result.errors.length > 0 && (
            <>
              <Typography variant="body2" color="error.main" sx={{ mt: 1, fontWeight: 'bold' }}>
                • Errores ({importResultDialog.result.errors.length}):
              </Typography>
              <Box sx={{ maxHeight: 150, overflow: 'auto', mt: 1 }}>
                {importResultDialog.result.errors.map((err, idx) => (
                  <Typography key={idx} variant="caption" display="block" color="error.main">
                    {err}
                  </Typography>
                ))}
              </Box>
            </>
          )}
          {importResultDialog && importResultDialog.result.missing_active_products.length > 0 && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              {importResultDialog.result.missing_active_products.length} producto(s) activo(s) siguen sin precio en
              esta lista - entrá a la lista para ver el detalle.
            </Typography>
          )}
          {importResultDialog && importResultDialog.result.cross_list_alerts.length > 0 && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Hay avisos de precios que también existen en otras listas - entrá a la lista para verlos.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          {importResultDialog && (
            <Button onClick={() => goToDetail(importResultDialog.list)} color="primary">
              Ver lista
            </Button>
          )}
          <Button onClick={() => setImportResultDialog(null)} variant="contained">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
